import { useAction, useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button, CircularLoader, CloseIcon } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import {
  FadeOverflow,
  InfoMessage,
  mediaMaxMobile,
  UnderlineTabs,
} from '@root/shared/ui-kit'

import { supportDialogScreenAtom } from '../atoms'
import { supportTicketsModelAdmin } from '../atoms/reatom-support-ticket-admin'

import { SupportTicketsItemAdmin } from './support-tickets-item-admin'
import { SupportWelcomeHeader } from './support-welcome-header'

import type {
  SupportTicket,
  SupportTicketAdminType,
} from '../model/support-ticket'

type SupportTicketsAdminProps = { onClose?: () => void }

export const SupportTicketsAdmin: React.FC<SupportTicketsAdminProps> = ({
  onClose,
}) => {
  const { t } = useTranslation()

  const [ticketsState] = useAtom(supportTicketsModelAdmin)
  const [type, setType] = useAtom(supportTicketsModelAdmin.ticketType)
  const [, setScreen] = useAtom(supportDialogScreenAtom)
  const refresh = useAction(supportTicketsModelAdmin.refresh)

  const [totals] = useAtom(supportTicketsModelAdmin.totals)

  const handleTabChange = (value: SupportTicketAdminType) => setType(value)
  const handleRetry = () => refresh()

  const renderContent = () =>
    AsyncStateN.match(ticketsState, {
      NotInitiated: () => (
        <RootEmpty>
          <CircularLoader size={22} color="gray" />
        </RootEmpty>
      ),
      Loading: () => (
        <RootEmpty>
          <CircularLoader size={22} color="gray" />
        </RootEmpty>
      ),
      Success: ({ data: tickets }) => {
        if (tickets.length === 0) {
          return (
            <RootEmpty>
              <InfoMessage
                intent="empty"
                title={t('common.no-results')}
                retryText={t('common.refresh')}
                onRetry={handleRetry}
              />
            </RootEmpty>
          )
        }

        return (
          <FadeOverflowList
            mode="always"
            variant="vertical"
            blockStart={6}
            blockEnd={6}
          >
            {tickets.map((ticket: SupportTicket) => (
              <SupportTicketsItemAdmin
                key={ticket.id}
                ticket={ticket}
                onSelect={() => {
                  setScreen({
                    screen: 'conversation',
                    ticket,
                  })
                }}
              />
            ))}
          </FadeOverflowList>
        )
      },
      Error: () => (
        <RootEmpty>
          <InfoMessage intent="error" onRetry={handleRetry} />
        </RootEmpty>
      ),
    })

  return (
    <Root>
      <SupportWelcomeTop>
        <SupportWelcomeHeader online />
        {onClose ? (
          <ButtonClose
            size="s"
            variant="ghost"
            iconRight={<CloseIcon />}
            onClick={onClose}
          />
        ) : undefined}
      </SupportWelcomeTop>
      <TicketsList>
        <Background>
          <AdminTabs value={type} onChange={handleTabChange}>
            <AdminTabsItem value={'new' satisfies SupportTicketAdminType}>
              {t('support.tickets-admin.unread-tickets')}
              {totals.unreadTickets ? (
                <>
                  &nbsp;(<TabCountSlot>{totals.unreadTickets}</TabCountSlot>)
                </>
              ) : undefined}
            </AdminTabsItem>
            <AdminTabsItem value={'active' satisfies SupportTicketAdminType}>
              {t('support.tickets-admin.active-tickets')}
              {totals.activeTickets ? (
                <>
                  &nbsp;(<TabCountSlot>{totals.activeTickets}</TabCountSlot>)
                </>
              ) : undefined}
            </AdminTabsItem>
            <AdminTabsItem value={'closed' satisfies SupportTicketAdminType}>
              {t('support.tickets-admin.closed-tickets')}
              {totals.closedTickets ? (
                <>
                  &nbsp;(<TabCountSlot>{totals.closedTickets}</TabCountSlot>)
                </>
              ) : undefined}
            </AdminTabsItem>
          </AdminTabs>
          {renderContent()}
        </Background>
      </TicketsList>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  min-width: 0;
  height: 600px;
`
const RootEmpty = styled.div`
  display: grid;
  place-content: center;
  flex: 1;
  min-height: 0;
  padding: 50px 0;
`

const SupportWelcomeTop = styled.div`
  padding-block: 30px 20px;
  padding-inline: 39px;

  ${mediaMaxMobile(css`
    padding-inline: 30px;
  `)}
`

const TicketsList = styled.div`
  padding: 3px;
  min-height: 0;
`

const AdminTabs = styled(UnderlineTabs<SupportTicketAdminType>)`
  --tab-height: 34px;

  margin-inline-end: 10px;
  margin-bottom: 6px;
`

const AdminTabsItem = styled(UnderlineTabs.Tab)`
  padding: 0;
  font-size: 11px;
`

const TabCountSlot = styled.span`
  display: inline-block;
  /* min-width: 3ch; */
  /* font-variant-numeric: tabular-nums; */
`

const FadeOverflowList = styled(FadeOverflow)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  align-content: start;

  padding-inline-end: 6px;
  padding-block-end: 6px;
`

const Background = styled.div`
  background: var(--appearanceA-5);
  border-radius: 10px;
  padding: 10px;
  height: 100%;
  width: 100%;
  padding-inline-end: 0;
  position: relative;

  display: flex;
  flex-direction: column;
  min-height: 0;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 42px;
    width: 100%;
    height: 2px;
    z-index: -1;
    background: #222;
  }
`

const ButtonClose = styled(Button)`
  color: var(--text-9);

  position: absolute;
  right: 0;
  top: 6px;
  z-index: 1;

  &:hover {
    color: var(--text-12);
    background: transparent;
  }
`
